// =============================================================================
// client/src/lib/flux/editor-config.js | v1.0 | S90 | P12A item 7
// =============================================================================
// The ONE definition of what the campaign body editor is allowed to produce.
//
// 🔴 THE REQUIREMENT IS NOT "A RICH TEXT EDITOR". It is deterministic stored
// HTML for a send-time merge pipeline that a football office authors by hand.
// A schema-based editor is the right shape for that precisely because the
// allowed formatting surface is DECLARED here rather than discovered in
// whatever a browser happened to paste — anything not in the schema is dropped
// on ingest rather than stored and mailed to a coach.
//
// WHY TIPTAP (ProseMirror), recorded so nobody re-litigates it cold:
//  · RecruitMail had NO editor. Searched RecruitMail_Technical_Legacy_Spec_v1.md
//    end to end — the only "editor" hits are the Supabase/SQL editor. So there
//    is no legacy behaviour to preserve and the choice was free.
//  · Schema-based, so paste sanitization is a property of the schema rather
//    than a blocklist someone has to maintain.
//  · insertContentAt() is what item 6's cursor-position insertion needs.
//  · Undo/redo comes from prosemirror-history, not from us.
//  · MIT, including Link. TipTap's paid tier is Collaboration/AI/Pro and none
//    of items 6-9 needs any of it. Verified against the registry, not assumed.
//  Lexical was the serious alternative and would also have worked; TipTap won
//  on insertContentAt and on the schema being the sanitizer.
//
// ⚠ StarterKit v3 ALREADY BUNDLES Link. Installing @tiptap/extension-link
// alongside it produces `Duplicate extension names found: ['link']` and two
// extensions fighting over the same mark. Configure it THROUGH StarterKit, as
// below. (@tiptap/extension-link is in package.json only as StarterKit's own
// transitive dependency; do not import it directly.)
// =============================================================================

import StarterKit from '@tiptap/starter-kit';

// 🔴 BARE ANCHORS, DELIBERATELY. TipTap's Link default injects
// target="_blank" rel="noopener noreferrer nofollow" into every <a>. Measured
// against the real stored body on 4818b741: that alone grew it 538B -> 585B and
// was the ENTIRE difference between stored and round-tripped bytes.
//
// Those attributes are harmless in an inbox, but they are still a change to
// recruit-facing output made by a tool the operator did not ask to change it,
// applied silently to every link in every campaign. Turning them off makes an
// anchor round-trip exactly as authored, which is the property that lets an
// existing campaign be opened in the new editor without its body moving.
export const CAMPAIGN_BODY_EXTENSIONS = [
  StarterKit.configure({
    link: { openOnClick: false, HTMLAttributes: { target: null, rel: null } },
  }),
];

// ─── SEND-TIME ARTIFACTS MUST NEVER BE INGESTED AS BODY CONTENT ──────────────
// flux-engine.js · appendUnsubscribeLink appends, at SEND time, to a COPY:
// a collapsed preheader suppressor, the intentional 1200px spacer, and the
// unsubscribe footer. None of it is stored in template_variants[0].body, so the
// editor cannot meet it on a normal load.
//
// 🔴 IT CAN MEET IT ON A PASTE. If anyone pastes a delivered email back in:
//   · the 1200px spacer is DROPPED — the schema has no div node. Safe by
//     construction, and NOT by anything we wrote.
//   · the unsubscribe ANCHOR SURVIVES — <a> is in the schema. The next send
//     then appends a second one and the coach gets two unsubscribe links.
// Measured, not reasoned: round-tripping appendUnsubscribeLink's own output
// keeps `flux/unsubscribe` and loses `1200px`.
//
// So the spacer needs no guard and the link does. This strips it on the way in.
const SEND_ARTIFACT_PATTERNS = [
  /<div[^>]*height:\s*1200px[\s\S]*?<\/div>/gi,          // the intentional spacer
  /<div[^>]*mso-hide:all[\s\S]*?<\/div>/gi,              // the preheader suppressor
  /<div[^>]*>\s*If you no longer wish to receive[\s\S]*?<\/div>/gi,  // the unsubscribe footer block
  /<a[^>]*\/api\/flux\/unsubscribe[^>]*>[\s\S]*?<\/a>/gi,            // a bare unsubscribe anchor
  /<img[^>]*\/api\/flux\/track\/open[^>]*>/gi,                       // the open pixel
];

export function stripSendTimeArtifacts(html) {
  let out = String(html || '');
  for (const rx of SEND_ARTIFACT_PATTERNS) out = out.replace(rx, '');
  return out;
}
